import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DataTableSchema } from '../../../model/dynamic-data-table.model';
import { DynamicFormArray, FieldSelectedValue, FormFieldConfig, INFO_TYPES, InfoFieldConfig, Props } from '../../../model/form.model';
import { BreadcrumsComponent } from "../../../components/breadcrums/breadcrums.component";
import { API_STATUS, ERROR_RESPONSE, SearchConfig } from '../../../model/api.model';
import { CommonUtils } from '../../../utils/common.utils';import { ExcelExportService } from '../../../services/excel-export.service';
import { CommonService } from '../../../services/common.service';
import { CamelCasePipe } from '../../../utils/camel-case.pipe';
import { DynamicDataTableComponent } from '../../../layout/dynamic-data-table/dynamic-data-table.component';
import { SearchContainerComponent } from '../../../layout/search-container/search-container.component';
import { SpinnerComponent } from '../../../layout/spinner/spinner.component';
import { EventZeroRegReport } from '../../../model/event-registration-schema.model';

@Component({
  selector: 'app-event-registration-dealer',
  standalone: true,
  imports: [SearchContainerComponent, DynamicDataTableComponent, CommonModule, BreadcrumsComponent, SpinnerComponent, CamelCasePipe],
  templateUrl: './event-registration-dealer.component.html',
  styleUrl: './event-registration-dealer.component.scss'
})
export class EventRegistrationDealerComponent implements OnInit, OnDestroy {

  @Input() DataTableSchema!: DataTableSchema;
  @Input() filterSchema!: DynamicFormArray;
  @Input() props!: Props;
  @Input() forSection?: boolean = false;
  @Input() apiConfig!: SearchConfig;
  @Input() elementId!: string;
  @Input() globalFilterFields!: Array<any>
  @Input() onLoadFetch = false;
  @Input() infoFields!: Array<InfoFieldConfig>;
  public beginRender = false;
  private readonly commonUtils = inject(CommonUtils);
  private readonly commonService = inject(CommonService);
  private readonly exportService = inject(ExcelExportService);
  private globalpayloadSubject!: any;
  private globalpayload: any;
  public spinnerName!: string;
  public globalTitleFields: any = {}
  public dealersWithZeroRegistrations = '';
  public EventZeroRegDataSchema = structuredClone(EventZeroRegReport);

  ngOnInit() {
    this.beginRender = true;
    if (this.forSection) {
      this.spinnerName = this.elementId;
      this.initializeSectionTable()
    }
    if (this.onLoadFetch) {
      this.fetchDataAndUpdateData()
    }
  }
  ngOnDestroy() {
    this.globalpayloadSubject?.unsubscribe()
  }
  initializeSectionTable() {
    this.updateFilterFields()
    this.updateTitle();
  }
  updateTitle(field?: FormFieldConfig) {
    if (this.DataTableSchema['titleSchema']) {
      const titleConfig = {
        titleSchema: this.DataTableSchema['titleSchema'],
        filterFields: this.filterSchema['FieldsArray'],
        globalTitleFields: this.globalTitleFields ?? {}
      }
      const titleResult = this.commonUtils.updateTitle(titleConfig, field);
      this.DataTableSchema['title'] = titleResult['title'];
    }
  }
  async updateFilterFields() {
    if (this.filterSchema.globalPayloadSubject) {
      this.globalpayloadSubject = this.filterSchema.globalPayloadSubject.subscribe(globalPayload => {
        if (this.globalFilterFields.includes(globalPayload['FieldName'])) {
          this.globalTitleFields = globalPayload?.Value?.title ?? {}
          if (globalPayload?.Value?.body) {
            this.globalpayload = structuredClone(globalPayload['Value']['body']);
            this.updateTitle()
            this.updatePayloadAndFetch(this.globalpayload)
          }
        }
      })
    }
    if (!this.filterSchema?.FieldsArray) {
      return;
    }
    this.filterSchema.FieldsArray.forEach(field => {
      this.commonUtils.setDefaultValueForOptionsField(field);
    })
    this.filterSchema.onControlValueChanged = this.filterFieldOnChange;
    this.updateTitle();
  }
  filterFieldOnChange = (event: FieldSelectedValue) => {
    let selectedField: FormFieldConfig;
    const postParams = this.globalpayload ?? {};
    event?.FieldArray?.forEach(field => {
      if (field['isDependentOn'] === event['FieldName']) {
        field['selectedValue'] = '';
      } else if (field['fieldName'] === event['FieldName']) {
        selectedField = field;
        this.updateTitle(selectedField)
      }
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        postParams[field['fieldName']] = field['selectedValue']
      }
    })
    this.fetchDataAndUpdateData(postParams)
  }
  updatePayloadAndFetch(globalPayload: any) {
    const postParams = {
      ...globalPayload
    };
    if (this.filterSchema?.FieldsArray) {
      this.filterSchema?.FieldsArray.forEach(filterField => {
        if (filterField['selectedValue'] && filterField['selectedValue'] !== null && filterField['selectedValue'] !== '') {
          postParams[filterField['fieldName']] = filterField['selectedValue']
        }
      })
    }
    this.fetchDataAndUpdateData(postParams)
  }

  private getValueByKey(data: any, key: string): any {
    if (data[key] !== undefined) {
      return data[key];
    }
    const lowercaseKey = key.toLowerCase();
    for (const dataKey of Object.keys(data)) {
      if (dataKey.toLowerCase() === lowercaseKey) {
        return data[dataKey];
      }
    }
    return undefined;
  }

  processSpinIdsForDealers() {
    if (!this.DataTableSchema?.['dataSource'] || !this.DataTableSchema?.['columnSchema']) {
      return;
    }
    const hasSpinIdsColumn = this.DataTableSchema['columnSchema'].some((col: any) => col.fieldKey === 'spinIds');
    if (!hasSpinIdsColumn) {
      return;
    }

    this.DataTableSchema['dataSource'] = this.DataTableSchema['dataSource'].map((row: any) => {
      const registrationCount = this.getValueByKey(row, 'numberOfRegistration');
      if (registrationCount === 0 || registrationCount === '0') {
        const spinId = this.getValueByKey(row, 'spinId');
        if (spinId) {
          return { ...row, spinIds: Array.isArray(spinId) ? spinId : [spinId] };
        }
        return { ...row, spinIds: [] };
      }
      return { ...row, spinIds: [] };
    });
  }

  extractDealersWithZeroRegistrations() {
    if (!this.DataTableSchema?.dataSource) {
      this.dealersWithZeroRegistrations = '';
      return;
    }

    const hasNumberOfRegistrationColumn = this.DataTableSchema['columnSchema']?.some((col: any) => col.fieldKey === 'numberOfRegistration');
    const hasDealerCodeColumn = this.DataTableSchema['columnSchema']?.some((col: any) => col.fieldKey === 'dealerCode');

    if (!hasNumberOfRegistrationColumn || !hasDealerCodeColumn) {
      this.dealersWithZeroRegistrations = '';
      return;
    }

    const zeroRegDealers = this.DataTableSchema['dataSource']
      .filter((row: any) => {
        const registrationCount = this.getValueByKey(row, 'numberOfRegistration');
        return registrationCount === 0 || registrationCount === '0';
      })
    this.EventZeroRegDataSchema.dataSource = zeroRegDealers
  }

  updateInfoFields() {
    if (this.infoFields && this.DataTableSchema['dataSource']) {
      this.infoFields.forEach(field => {
        if (field['infoType'] === INFO_TYPES['Total'] || field['infoType'] === INFO_TYPES['Count']) {
          if (Array.isArray(field['calculateBy'])) {
            field['value'] = this.DataTableSchema['dataSource']?.reduce((sum: number, item: any) => {
              return sum + field['calculateBy'].reduce((innerSum: number, key: string | number) => {
                const value = this.getValueByKey(item, String(key));
                return innerSum + Number(value || 0);
              }, 0);
            }, 0);
          } else {
            field['value'] = this.DataTableSchema['dataSource']?.reduce((sum: number, item: any) => {
              const value = this.getValueByKey(item, field['calculateBy']);
              return sum + Number(value || 0);
            }, 0);
          }
        }
      })
    }
  }

  private setLoadingState(isLoading: boolean): void {
    if (this.DataTableSchema) {
      this.DataTableSchema.isLoading = isLoading;
    }
    if (this.EventZeroRegDataSchema) {
      this.EventZeroRegDataSchema.isLoading = isLoading;
    }
  }

  fetchDataAndUpdateData(postParam?: any) {
    if (this.apiConfig) {
      this.setLoadingState(true);
      const fetchParams: SearchConfig = {
        ...this.apiConfig,
      }
      if (this.DataTableSchema?.dataSource) {
        if (this.DataTableSchema.dataSource?.data) {
          this.DataTableSchema.dataSource.data = []
        } else {
          this.DataTableSchema.dataSource = []
        }
      }
      const originalBody = this.apiConfig.body || {};
      fetchParams['body'] ??= {};
      fetchParams['body'] = { ...originalBody, ...postParam };
      const urlPath = this.apiConfig['urlPath'] ?? '';
      return this.commonService.getDataFromDataSource(fetchParams, urlPath).then(response => {
        if (response['status'] === API_STATUS['Success']) {
          this.DataTableSchema['dataSource'] = response['result'];
        } else {
          this.commonUtils.showMatSnackBar(ERROR_RESPONSE['noDataFound'])
          this.DataTableSchema['dataSource'] = response['result'];
        }
        this.processSpinIdsForDealers();
        this.extractDealersWithZeroRegistrations();
        this.updateInfoFields();
      }).catch(err => {
        console.info('Error :-', err)  
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['noDataFound'])
      }).finally(() => {
        this.setLoadingState(false);
      });
    } else {
      this.setLoadingState(false);
      return Promise.reject(new Error('API Config issue.'));
    }
  }
  exportTableData() {
    let data: Array<any> = this.DataTableSchema['dataSource'];
    if (data.length <= 0) {
      this.commonUtils.showMatSnackBar('Export Failed - No Data Found')
      return;
    }
    const exportConfig: any = this.DataTableSchema?.exportConfig;
    if (exportConfig) {
      const sortedData = this.commonUtils.clientSideSort(this.DataTableSchema['dataSource'], this.DataTableSchema.activeSorts);
      data = structuredClone(this.commonUtils.formatExcelData(exportConfig, sortedData, this.filterSchema)) ?? [];
      const fileName = exportConfig.fileName ?? '';
      const description = exportConfig.descriptions ?? [];
      this.exportService.exportAsExcelFile(data, fileName, description)
    }
  }
}
